import { useState } from "react";
import { Sparkles, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import { notifyWhatsApp } from "@/lib/notify-whatsapp";

// Validation schema for contact form
const contactFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, "Name muss mindestens 2 Zeichen haben")
    .max(100, "Name darf maximal 100 Zeichen haben")
    .regex(/^[\p{L}\s\-'.]+$/u, "Name enthält ungültige Zeichen"),
  email: z
    .string()
    .trim()
    .email("Bitte gib eine gültige E-Mail-Adresse ein")
    .max(254, "E-Mail darf maximal 254 Zeichen haben"),
  phone: z
    .string()
    .trim()
    .max(20, "Telefonnummer darf maximal 20 Zeichen haben")
    .regex(/^[\d\s\-+().]*$/, "Telefonnummer enthält ungültige Zeichen")
    .optional()
    .or(z.literal("")),
  message: z
    .string()
    .trim()
    .max(5000, "Nachricht darf maximal 5000 Zeichen haben"),
  affiliateCode: z
    .string()
    .trim()
    .regex(/^[A-Z0-9]{5}$/, "Code muss 5-stellig sein (Buchstaben A-Z und Zahlen)")
    .optional()
    .or(z.literal("")),
});
const serviceOptions = [
  { id: "hausmeister", label: "Hausmeisterservice" },
  { id: "elektro", label: "Elektrotechnik" },
  { id: "cardetailing", label: "Car Detailing" },
  { id: "it", label: "IT Helpdesk" },
  { id: "garten", label: "Gartenarbeiten" },
  { id: "security", label: "Security Service" },
  { id: "winter", label: "Winterdienst" },
  { id: "reinigung", label: "Reinigungsservice" },
  { id: "sonstiges", label: "Sonstiges" },
];

const ContactForm = () => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [customerType, setCustomerType] = useState("");
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [otherService, setOtherService] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    subject: "",
    message: "",
    affiliateCode: "",
  });

  const handleServiceToggle = (serviceId: string) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId)
        ? prev.filter((s) => s !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Build full message with all details
      const serviceLabels = selectedServices
        .map((id) => serviceOptions.find((s) => s.id === id)?.label || id)
        .join(", ");
      
      const fullMessage = `
Kundentyp: ${customerType === "privat" ? "Privatkunde" : "Gewerblich"}
Gewünschte Dienstleistungen: ${serviceLabels}
${selectedServices.includes("sonstiges") && otherService ? `Sonstiges: ${otherService.slice(0, 500)}` : ""}
Betreff: ${formData.subject}

Nachricht:
${formData.message || "Keine zusätzliche Nachricht"}
      `.trim();

      // Validate form data with Zod before submission
      const validationResult = contactFormSchema.safeParse({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: fullMessage,
        affiliateCode: formData.affiliateCode,
      });

      if (!validationResult.success) {
        const firstError = validationResult.error.errors[0];
        toast({
          title: "Validierungsfehler",
          description: firstError.message,
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      const validatedData = validationResult.data;

      const { error } = await supabase.from("contact_submissions").insert({
        name: validatedData.name,
        email: validatedData.email,
        phone: validatedData.phone || null,
        message: validatedData.message,
        source: "angebot",
        affiliate_code: validatedData.affiliateCode || null,
      });

      if (error) throw error;

      // Fire-and-forget WhatsApp notification
      notifyWhatsApp({
        source: "angebot",
        name: validatedData.name,
        email: validatedData.email,
        phone: validatedData.phone || null,
        message: validatedData.message,
      });

      toast({
        title: "Anfrage gesendet!",
        description: "Vielen Dank für deine Anfrage. Wir melden uns in Kürze bei dir.",
      });

      // Reset form
      setStep(1);
      setCustomerType("");
      setSelectedServices([]);
      setOtherService("");
      setFormData({ name: "", phone: "", email: "", subject: "", message: "", affiliateCode: "" });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error submitting form:", error);
      }
      toast({
        title: "Fehler beim Senden",
        description: "Es gab ein Problem beim Senden deiner Anfrage. Bitte versuche es erneut.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceedToStep2 = customerType !== "";
  const canProceedToStep3 = selectedServices.length > 0;
  const canSubmit = formData.name && formData.email && formData.subject;

  return (
    <section id="contact" className="py-12 md:py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-4 md:mb-8 max-w-3xl mx-auto rounded-2xl md:rounded-3xl p-5 md:p-10 bg-sky border border-border/40">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Jetzt kostenlos & unverbindlich Angebot anfragen
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Beschreib uns deinen Bedarf und wir organisieren den passenden Dienstleister in Hanau und Umgebung.
          </p>
        </div>

        <div className="max-w-3xl mx-auto glass-card rounded-2xl md:rounded-3xl p-4 md:p-10">
          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 sm:gap-4 mb-5 md:mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div
                  className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                    step >= s
                      ? "gradient-mint text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {s}
                </div>
                {s < 3 && (
                  <div
                    className={`w-7 sm:w-12 h-1 rounded ${
                      step > s ? "bg-primary" : "bg-muted"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="glass-card rounded-xl md:rounded-3xl p-4 sm:p-6 md:p-8">
            {/* Step 1: Customer Type */}
            {step === 1 && (
              <div className="space-y-6 animate-fade-up">
                <h3 className="text-xl font-semibold text-foreground mb-4">
                  Schritt 1: Bist du Privat- oder Gewerbekunde?
                </h3>
                <RadioGroup value={customerType} onValueChange={setCustomerType}>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center space-x-3 p-4 rounded-xl border border-border hover:border-primary transition-colors cursor-pointer">
                      <RadioGroupItem value="privat" id="privat" />
                      <Label htmlFor="privat" className="text-lg cursor-pointer flex-1">
                        Privatkunde
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 p-4 rounded-xl border border-border hover:border-primary transition-colors cursor-pointer">
                      <RadioGroupItem value="gewerbe" id="gewerbe" />
                      <Label htmlFor="gewerbe" className="text-lg cursor-pointer flex-1">
                        Gewerblich
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
                <div className="flex justify-end pt-4">
                  <Button
                    type="button"
                    onClick={() => setStep(2)}
                    disabled={!canProceedToStep2}
                    className="gradient-mint text-primary-foreground"
                  >
                    Weiter <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Service Selection */}
            {step === 2 && (
              <div className="space-y-6 animate-fade-up">
                <h3 className="text-xl font-semibold text-foreground mb-4">
                  Schritt 2: Welche Dienstleistung benötigst du?
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {serviceOptions.map((service) => (
                    <label
                      key={service.id}
                      className={`flex items-center space-x-3 p-4 rounded-xl border transition-colors cursor-pointer ${
                        selectedServices.includes(service.id)
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <Checkbox
                        id={service.id}
                        checked={selectedServices.includes(service.id)}
                        onCheckedChange={() => handleServiceToggle(service.id)}
                      />
                      <span className="cursor-pointer flex-1">{service.label}</span>
                    </label>
                  ))}
                </div>
                {selectedServices.includes("sonstiges") && (
                  <Input
                    placeholder="Beschreib kurz deinen Bedarf..."
                    value={otherService}
                    onChange={(e) => setOtherService(e.target.value)}
                    className="mt-4"
                  />
                )}
                <div className="flex justify-between pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                  >
                    <ChevronLeft className="mr-2 h-4 w-4" /> Zurück
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setStep(3)}
                    disabled={!canProceedToStep3}
                    className="gradient-mint text-primary-foreground"
                  >
                    Weiter <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Contact Details */}
            {step === 3 && (
              <div className="space-y-6 animate-fade-up">
                <h3 className="text-xl font-semibold text-foreground mb-4">
                  Schritt 3: Deine Kontaktdaten
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      placeholder="Dein Name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">E-Mail *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="ihre@email.de"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Telefon</Label>
                    <Input
                      id="phone"
                      placeholder="Deine Telefonnummer"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject">Betreff *</Label>
                    <Input
                      id="subject"
                      placeholder="Worum geht es?"
                      value={formData.subject}
                      onChange={(e) =>
                        setFormData({ ...formData, subject: e.target.value })
                      }
                      required
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="affiliateCode">Empfehlungscode (optional, 5-stellig)</Label>
                  <Input
                    id="affiliateCode"
                    maxLength={5}
                    placeholder="z.B. AB12C"
                    value={formData.affiliateCode}
                    onChange={(e) =>
                      setFormData({ ...formData, affiliateCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5) })
                    }
                    className="mt-1 font-mono tracking-widest"
                  />
                </div>
                <div>
                  <Label htmlFor="message">Nachricht</Label>
                  <Textarea
                    id="message"
                    placeholder="Beschreib dein Anliegen..."
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    rows={4}
                    className="mt-1"
                  />
                </div>
                <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(2)}
                    className="w-full sm:w-auto"
                  >
                    <ChevronLeft className="mr-2 h-4 w-4" /> Zurück
                  </Button>
                  <Button
                    type="submit"
                    disabled={!canSubmit || isSubmitting}
                    className="w-full sm:w-auto gradient-mint text-primary-foreground shadow-glow hover:scale-[1.02] transition-transform whitespace-normal"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird gesendet...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-4 w-4" /> Angebot anfordern
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
